<template>
    <div class="home-equipments-page">
        <gh-breadcrumb :node-list="nodeList"></gh-breadcrumb>
        <div class="gh-container">
            <div class="clip-box banner">
                <img :src="bannerImageUrl" alt="關於健豪" />
                <section>
                    <span class="text-overlay">關於健豪</span>
                </section>
            </div>
            <div class="content_outer">
                <div class="content_inner">
                    <div class="tab-list-wrapper main-item" id="equipTabs">
                        <gh-button
                            v-for="intro in aboutTab"
                            :key="intro.node"
                            :variant="buttonVariant.Lake"
                            :class="{ active: activeNode === intro.node }"
                            style="flex: 1"
                            @click="gotoAboutTab(intro.node)"
                        >
                            <span>{{ intro.name }}</span>
                        </gh-button>
                    </div>

                    <equip-card
                        v-if="activeNode === 'intros'"
                        key="home-equipments-intros-card"
                        :title="activeName"
                        :has-avatar="true"
                    >
                        <template #avatar>
                            <div class="avatar-wrapper avatar-wrapper-lg">
                                <div class="avatar-image clip-box">
                                    <img
                                        :src="generalManagerAvatarImageUrl"
                                        :alt="generalManagerTitle"
                                    />
                                </div>
                                <div class="avatar-title">
                                    <span>{{ generalManagerTitle }}</span>
                                </div>
                            </div>
                        </template>
                        <template #content>
                            <p
                                v-for="(value, index) in intros"
                                :key="`intro-${index}`"
                                class="equip-phase"
                            >
                                {{ value }}
                            </p>
                            <div class="signature-row equip-phase">
                                <span>總經理</span>
                                <div class="clip-box signature-image">
                                    <img
                                        :src="generalManagerSignatureImageUrl"
                                        alt="總經理簽名"
                                    />
                                </div>
                            </div>
                        </template>
                    </equip-card>

                    <equip-card
                        v-if="activeNode === 'history'"
                        key="home-equipments-history-card"
                        :title="activeName"
                    >
                        <template #tab>
                            <div
                                class="tab-list-wrapper start history-tab-list"
                            >
                                <gh-button
                                    v-for="bp in historyBreakpoints"
                                    :key="bp.id"
                                    :variant="buttonVariant.Link"
                                    :class="{
                                        active: currentBreakPoint === bp.id,
                                    }"
                                    class="history-btn"
                                    @click="handleBreakpoint(bp.id)"
                                >
                                    <span>{{ bp.max }}~{{ bp.min }}</span>
                                </gh-button>
                            </div>
                        </template>
                        <template #content>
                            <template
                                v-for="ev in historyContent"
                                :key="`history-${ev.year}`"
                            >
                                <p class="equip-title">{{ ev.year }}</p>
                                <p class="equip-phase">
                                    {{ ev.content.join('\r\n') }}
                                </p>
                            </template>
                        </template>
                    </equip-card>

                    <equip-card
                        v-if="activeNode === 'equipments'"
                        key="home-equipments-equipments-card"
                        :title="activeName"
                        style="padding-inline: 16px"
                    >
                        <template #tab>
                            <div class="tab-list-wrapper">
                                <gh-button
                                    v-for="equip in equipments"
                                    :key="equip.type"
                                    :variant="buttonVariant.White"
                                    :class="{
                                        active: currentEquipment === equip.type,
                                    }"
                                    class="equipment-btn"
                                    @click="handleEquipment(equip.type)"
                                >
                                    <span>{{ equip.type }}</span>
                                </gh-button>
                            </div>
                        </template>
                        <template #content>
                            <div class="equipment-desc">
                                <span>{{ currentEquipmentDesc }}</span>
                            </div>
                            <div class="equipment-preview">
                                <div
                                    v-for="item in equipmentContent"
                                    :key="`${currentEquipment}-${item.image}`"
                                    class="equipment-card-container"
                                >
                                    <div class="equipment-card">
                                        <div class="equipment-card-header">
                                            <img
                                                :src="
                                                    buildHomeEquipmentImageUrl(
                                                        currentEquipment,
                                                        item.image
                                                    )
                                                "
                                                alt="equipment"
                                            />
                                        </div>
                                        <div class="equipment-card-body">
                                            <div
                                                class="equipment-card-title"
                                                :title="item.title"
                                            >
                                                {{ item.title }}
                                            </div>
                                            <ellipsis-text
                                                v-if="item.text.length > 0"
                                                :text="item.text.join('\r\n')"
                                                :show-more="true"
                                                @click="
                                                    openDetail(
                                                        item.title,
                                                        item.text.join('\r\n')
                                                    )
                                                "
                                            ></ellipsis-text>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </equip-card>
                </div>
            </div>
        </div>
        <gh-modal ref="modalRef"></gh-modal>
    </div>
</template>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
    import EllipsisText from '@/components/base/EllipsisText.vue';
    import GhBreadcrumb from '@/components/base/GhBreadcrumb.vue';
    import GhButton from '@/components/base/GhButton.vue';
    import GhModal from '@/components/base/GhModal.vue';
    import EquipCard from '@/components/equipments/EquipCard.vue';
    import { ButtonVariant } from '@/types/components/base-components.types';
    import type { HomeEquipmentsTabNode } from './home-equipments.shared';
    import {
        HOME_EQUIPMENTS_BANNER_IMAGE_URL,
        HOME_EQUIPMENTS_CONTENT,
        HOME_EQUIPMENTS_DEFAULT_ACTIVE_TAB,
        HOME_EQUIPMENTS_DEFAULT_BREAKPOINT_ID,
        HOME_EQUIPMENTS_DEFAULT_EQUIPMENT_TYPE,
        HOME_EQUIPMENTS_GENERAL_MANAGER_AVATAR_IMAGE_URL,
        HOME_EQUIPMENTS_GENERAL_MANAGER_SIGNATURE_IMAGE_URL,
        HOME_EQUIPMENTS_GENERAL_MANAGER_TITLE,
        HOME_EQUIPMENTS_HISTORY_BREAKPOINTS,
        HOME_EQUIPMENTS_TABS,
        buildHomeEquipmentImageUrl,
        buildHomeEquipmentsBreadcrumbNodeList,
        filterHomeEquipmentsHistoryContent,
        isHomeEquipmentsTabNode,
        resolveHomeEquipmentContent,
        resolveHomeEquipmentDescription,
        resolveHomeEquipmentsActiveName,
    } from './home-equipments.shared';

    type GhModalInstance = {
        open: (options?: {
            title?: string;
            body?: string;
            isStatic?: boolean;
        }) => void;
    };

    // ----- state -----
    const modalRef = ref<GhModalInstance | null>(null);
    const activeNode = ref<HomeEquipmentsTabNode>(
        HOME_EQUIPMENTS_DEFAULT_ACTIVE_TAB
    );
    const currentBreakPoint = ref(HOME_EQUIPMENTS_DEFAULT_BREAKPOINT_ID);
    const currentEquipment = ref(HOME_EQUIPMENTS_DEFAULT_EQUIPMENT_TYPE);
    const aboutTab = HOME_EQUIPMENTS_TABS;
    const intros = HOME_EQUIPMENTS_CONTENT.intros;
    const historyBreakpoints = HOME_EQUIPMENTS_HISTORY_BREAKPOINTS;
    const equipments = HOME_EQUIPMENTS_CONTENT.equipments;
    const bannerImageUrl = HOME_EQUIPMENTS_BANNER_IMAGE_URL;
    const generalManagerAvatarImageUrl =
        HOME_EQUIPMENTS_GENERAL_MANAGER_AVATAR_IMAGE_URL;
    const generalManagerSignatureImageUrl =
        HOME_EQUIPMENTS_GENERAL_MANAGER_SIGNATURE_IMAGE_URL;
    const generalManagerTitle = HOME_EQUIPMENTS_GENERAL_MANAGER_TITLE;
    const buttonVariant = ButtonVariant;

    // ----- computed -----
    const activeName = computed(() => {
        return resolveHomeEquipmentsActiveName(activeNode.value);
    });

    const nodeList = computed(() => {
        return buildHomeEquipmentsBreadcrumbNodeList(activeNode.value);
    });

    const historyContent = computed(() => {
        return filterHomeEquipmentsHistoryContent(currentBreakPoint.value);
    });

    const currentEquipmentDesc = computed(() => {
        return resolveHomeEquipmentDescription(currentEquipment.value);
    });

    const equipmentContent = computed(() => {
        return resolveHomeEquipmentContent(currentEquipment.value);
    });

    // ----- methods -----
    const syncActiveTabFromHash = (): void => {
        const hash = window.location.hash.replace('#', '');
        if (!isHomeEquipmentsTabNode(hash)) {
            return;
        }

        activeNode.value = hash;
    };

    const scrollToEquipTabs = (): void => {
        const target = document.getElementById('equipTabs');
        if (!(target instanceof HTMLElement)) {
            return;
        }

        target.scrollIntoView({
            behavior: 'smooth',
        });
    };

    const gotoAboutTab = (node: HomeEquipmentsTabNode): void => {
        activeNode.value = node;
        if (window.location.hash !== `#${node}`) {
            window.location.hash = node;
        }

        scrollToEquipTabs();
    };

    const handleBreakpoint = (id: string): void => {
        currentBreakPoint.value = id;
    };

    const handleEquipment = (type: string): void => {
        currentEquipment.value = type;
    };

    const openDetail = (title: string, text: string): void => {
        modalRef.value?.open({
            title,
            body: text,
            isStatic: true,
        });
    };

    const handleHashChange = (): void => {
        syncActiveTabFromHash();
    };

    // ----- lifecycle -----
    onMounted(() => {
        syncActiveTabFromHash();
        window.addEventListener('hashchange', handleHashChange);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('hashchange', handleHashChange);
    });
</script>

<style lang="scss" scoped>
    .home-equipments-page {
        :deep(.gh-modal-body),
        :deep(.gh-modal-body-scroll) {
            text-align: justify;
        }

        .gh-container {
            display: flex;
            flex-direction: column;
        }

        .clip-box {
            position: relative;
            overflow: hidden;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            &.banner {
                height: 304px;

                img {
                    object-position: center 75%;
                }

                .text-overlay {
                    position: absolute;
                    top: 50%;
                    left: calc((100% - 1200px) / 2);
                    transform: translateY(-50%);
                    color: #ffffff;
                    font-size: 32px;
                    font-weight: 700;
                    transition: left 0.3s ease-out;

                    @media (max-width: 1400px) {
                        left: calc((100% - 1000px) / 2);
                    }

                    @media (max-width: 1200px) {
                        left: calc((100% - 80%) / 2);
                    }

                    @media (max-width: 1000px) {
                        left: calc((100% - 90%) / 2);
                    }

                    @media (max-width: 767px) {
                        left: 50%;
                        transform: translateX(-50%) translateY(-50%);
                    }
                }
            }
        }

        #equipTabs {
            scroll-margin-top: 85px;
        }

        .tab-list-wrapper,
        :deep(.equip-tab-list .tab-list-wrapper) {
            display: flex;
            justify-content: center;
            gap: 16px;
        }

        .tab-list-wrapper :deep(button),
        :deep(.equip-tab-list .tab-list-wrapper button) {
            height: auto;
            white-space: nowrap;
        }

        .tab-list-wrapper :deep(.history-btn),
        :deep(.equip-tab-list .tab-list-wrapper .history-btn) {
            min-width: 96px;
            padding-inline: 8px;
            border-radius: 0;
            border-top: none;
            border-left: none;
            border-right: none;
            border-width: 2px;
        }

        .tab-list-wrapper :deep(.history-btn:hover:not(:active):not(.active)),
        :deep(
            .equip-tab-list
                .tab-list-wrapper
                .history-btn:hover:not(:active):not(.active)
        ) {
            border-radius: 8px;
        }

        .tab-list-wrapper :deep(.equipment-btn),
        :deep(.equip-tab-list .tab-list-wrapper .equipment-btn) {
            padding: 8px 16px;
            font-size: 14px;
            border-radius: 100px;

            @media (max-width: 399px) {
                padding: 8px 12px;
            }
        }

        .tab-list-wrapper.main-item {
            padding-block: 16px;

            @media (max-width: 767px) {
                padding-inline: 16px;
                background: #f4f4f4;
            }
        }

        .tab-list-wrapper.start,
        :deep(.equip-tab-list .tab-list-wrapper.start) {
            @media (max-width: 767px) {
                justify-content: flex-start;
            }
        }

        .history-tab-list,
        :deep(.equip-tab-list .history-tab-list) {
            margin-bottom: 16px;
        }

        .avatar-wrapper {
            display: flex;
            flex-direction: column;
            gap: 16px;

            &.avatar-wrapper-lg {
                .avatar-image {
                    height: 380px;
                    border-radius: 8px;
                    align-self: center;

                    img {
                        position: relative;
                        top: -10%;
                        height: 150%;
                        object-position: 70% center;
                    }

                    @media (max-width: 767px) {
                        width: 256px;
                        height: 210px;

                        img {
                            height: 300%;
                            object-position: 60% -20px;
                        }
                    }

                    @media (max-width: 400px) {
                        height: 210px;
                    }
                }

                .avatar-title {
                    color: #454545;
                    font-size: 16px;
                    text-align: center;
                }
            }
        }

        .signature-row {
            display: flex;
            gap: 16px;
            justify-content: end;
        }

        .signature-image {
            width: 111px;

            img {
                object-position: center 100%;
            }
        }

        .equip-title {
            color: #086efb;
            font-size: 20px;
            font-weight: 700;
        }

        .equip-phase {
            white-space: pre-line;

            &:not(:last-child) {
                margin-bottom: 24px;
            }
        }

        .equipment-desc {
            margin-bottom: 16px;
            text-align: center;
        }

        .equipment-preview {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            width: 100%;
            gap: 16px;

            @media (max-width: 767px) {
                grid-template-columns: repeat(2, 1fr);
            }
        }

        .equipment-card-container {
            width: 100%;
            height: 100%;
        }

        .equipment-card {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .equipment-card-header {
            text-align: center;

            img {
                width: 100%;
            }
        }

        .equipment-card-body {
            display: flex;
            flex-direction: column;
            gap: 8px;

            .equipment-card-title {
                color: #333333;
                font-size: 18px;
                font-weight: 700;
                text-align: start;
                overflow: hidden;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
            }

            :deep(.ellipsis-text) {
                text-align: justify;
            }
        }
    }
</style>
